<template>
  <div class="despesas-page">
    <div class="action-buttons">
      <Button
        data-testid="despesas-nova-button"
        @click="openModal"
        :disabled="isLoading"
        severity="primary"
        :label="t('Nova despesa')"
        icon="pi pi-plus"
      />
      <Button
        data-testid="despesas-import-csv-button"
        @click="iniciaCsvImport"
        :disabled="isLoading"
        severity="primary"
        :label="t('Importar de CSV')"
        icon="pi pi-upload"
      />
      <Button
        data-testid="despesas-refresh-button"
        @click="loadData"
        :disabled="isLoading"
        severity="primary"
        size="small"
        :label="t('Atualizar')"
        :icon="isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'"
      />
    </div>

    <!-- Bulk Action Toolbar -->
    <div v-if="selectedDespesas.length > 0" class="bulk-action-toolbar">
      <div class="bulk-selection-info">
        <i class="pi pi-check-square"></i>
        <span>{{ selectedDespesas.length }} {{ t('selecionado(s)') }}</span>
        <span class="bulk-selection-amount">
          ({{ n(selectedTotalValor, 'currency') }})
        </span>
      </div>
      <div class="bulk-actions">
        <Button
          data-testid="despesas-clear-selection-button"
          :label="t('Limpar Seleção')"
          icon="pi pi-times"
          severity="secondary"
          outlined
          size="small"
          @click="clearSelection" />
        <Button
          data-testid="despesas-bulk-baixar-button"
          :label="t('Baixar Selecionadas')"
          icon="pi pi-check"
          severity="success"
          size="small"
          :loading="isBulkProcessing"
          @click="confirmBulkBaixa" />
      </div>
    </div>

    <!-- DataTable -->
    <data-table
      ref="dataTableRef"
      :config="datatableConfig"
      :data="tableData"
      :filterConfig="filterConfig"
      v-model:selection="selectedDespesas"
      @row-click="handleRowClick"
      @edit-click="editItem"
      @button-click="handleButtonClick"
      @selection-change="handleSelectionChange">
    </data-table>


    <!-- DespesasModal is centralized in ModalManager.vue - opened via eventBus -->
    <!-- DespesasCsvImportModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useDespesas } from '../../composables/useDespesas';
import { useConfirmAction } from '../../composables/useConfirmAction';
import type { DespesaListItem } from '../../types/despesas.types';

interface DataTableInstance {
  clearSelection: () => void;
}

interface ButtonClickPayload {
  value: number;
  data?: DespesaListItem;
  action: string;
}

interface SelectionChangePayload {
  selection: DespesaListItem[];
  added?: DespesaListItem[];
  removed?: DespesaListItem[];
}

interface BulkResults {
  success: number[];
  errors: Array<{ despesa_id: number; error: string }>;
}

const { t, n } = useI18n();
const { confirmAction } = useConfirmAction();
const { fetchDespesas, baixarDespesa } = useDespesas();

const tableData = ref<DespesaListItem[]>([]);
const isLoading = ref<boolean>(false);
const dataTableRef = ref<DataTableInstance | null>(null);
const selectedDespesas = ref<DespesaListItem[]>([]);
const isBulkProcessing = ref<boolean>(false);

// Computed: Total valor for selected despesas
const selectedTotalValor = computed<number>(() => {
  return selectedDespesas.value.reduce((sum, d) => {
    const valor = parseFloat(String(d.valor)) || parseFloat(String(d.despesa_valor)) || 0;
    return sum + valor;
  }, 0);
});

// Watch: Filter out already "baixado" despesas from selection (status != 1 means not pending)
watch(selectedDespesas, (newVal) => {
  const filtered = newVal.filter(d => String(d.despesa_status_id) === '1');
  if (filtered.length !== newVal.length) {
    log('DespesasPage', 'watch:selectedDespesas', 'Filtered out non-pending despesas', {
      before: newVal.length,
      after: filtered.length
    });
    selectedDespesas.value = filtered;
  }
}, { deep: true });

// Computed properties

const tableColumns = computed(() => {
  const columns = [
    {
      data: 'despesa_id',
      title: t('ID'),
      footerType: 'count',
      renderType: 'clickable_id',
      mergedColumns: [
        { data: 'franquia', title: t('Franquia') }
      ]
    },
    {
      data: 'status',
      title: t('Status')
    },
    {
      data: 'titulo',
      title: t('Título'),
      mergedColumns: [
        { data: 'categoria', title: t('Categoria') }
      ]
    },
    {
      data: 'valor',
      title: t('Valor'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'despesa_data_vencimento',
      title: t('Data Vencimento'),
      renderType: 'date',
      dataType: 'date'
    },
    {
      data: 'despesa_data_lancamento',
      title: t('Data Lançamento'),
      renderType: 'datetime',
      dataType: 'date'
    },
    {
      data: 'parcela',
      title: t('Parcela')
    },
    {
      data: 'despesa_id',
      title: t('Baixar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Baixar'),
      buttonIcon: 'pi pi-check',
      buttonClass: 'p-button-success p-button-sm',
      buttonAction: 'baixar',
      buttonCondition: (rowData: DespesaListItem) => String(rowData.despesa_status_id) === '1'
    }
  ];
  return columns;
});

// Row class function - mark non-pending despesas as not selectable
const getRowClass = (data: DespesaListItem): string => {
  return String(data.despesa_status_id) !== '1' ? 'row-not-selectable' : '';
};

const datatableConfig = computed(() => {
  const config = {
    tableId: 'despesasDataTable',
    columns: tableColumns.value,
    pagination: true,
    pageLength: 25,
    filterDisplay: 'row',
    heightMode: 'fill-viewport',
    footerLabel: t('Totais'),
    rowClass: getRowClass,
    checkbox: {
      enabled: true,
      position: 'first',
      frozen: false,
      width: '3rem'
    }
  };
  return config;
});

const filterConfig = computed(() => {
  return {
    columns: {
      'despesa_id': 'text',
      'status': 'select',
      'titulo': 'text',
      'valor': 'number',
      'data_vencimento': 'date',
      'data_lancamento': 'date',
      'parcela': 'text'
    }
  };
});

// Methods
const loadData = async (): Promise<void> => {
  log('DespesasPage', 'loadData', 'Begin load');
  try {
    isLoading.value = true;
    eventBus.emit('loading.show', t('Carregando despesas...'));

    const despesas = await fetchDespesas();

    tableData.value = despesas;
    log('DespesasPage', 'loadData', 'Loaded records', despesas.length);

    eventBus.emit('globalMessage.success', t('Carregados') + ` ${despesas.length} ` + t('registros de despesas'));
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('DespesasPage', 'loadData', 'Error', message);
    eventBus.emit('globalMessage.error', t('Erro ao carregar despesas: ') + message);
  } finally {
    isLoading.value = false;
    eventBus.emit('loading.hide');
  }
};

const editItem = (despesaId: number): void => {
  log('DespesasPage', 'editItem', 'Editing ID via eventBus', despesaId);
  eventBus.emit('modal.despesas.open', { id: despesaId });
};

const baixarItem = (despesaId: number): void => {
  log('DespesasPage', 'baixarItem', 'Baixar ID', despesaId);
  darBaixa(despesaId);
};

const darBaixa = (despesaId: number): void => {
  log('DespesasPage', 'darBaixa', 'Attempting baixa for ID', despesaId);

  confirmAction({
    message: t('Tem certeza que deseja dar baixa nesta despesa?'),
    header: t('Confirmação'),
    icon: 'pi pi-question-circle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      try {
        log('DespesasPage', 'darBaixa', 'Starting baixa for ID', despesaId);

        eventBus.emit('loading.show', t('Dando baixa na despesa...'));

        await baixarDespesa(despesaId);

        log('DespesasPage', 'darBaixa', 'Baixa successful for ID', despesaId);
        eventBus.emit('globalMessage.success', t('Baixa realizada com sucesso!'));
        eventBus.emit('despesa.saved');

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        log('DespesasPage', 'darBaixa', 'Error', message);
        console.error('Error giving baixa:', error);
        eventBus.emit('globalMessage.error', t('Erro ao dar baixa na despesa: ') + message);
      } finally {
        eventBus.emit('loading.hide');
      }
    }
  });
};

const handleRowClick = (event: unknown): void => {
  const rowData = event as DespesaListItem | undefined;
  log('DespesasPage', 'handleRowClick', 'Row clicked', rowData?.despesa_id || null);
};

const handleButtonClick = (payload?: ButtonClickPayload): void => {
  if (!payload) return;
  const { value, action } = payload;
  log('DespesasPage', 'handleButtonClick', 'Button clicked', { value, action });

  switch (action) {
    case 'baixar':
      darBaixa(value);
      break;
    default:
      log('DespesasPage', 'handleButtonClick', 'Unknown button action', action);
  }
};

const handleSelectionChange = (payload?: SelectionChangePayload): void => {
  if (!payload) return;
  const { selection, added, removed } = payload;
  log('DespesasPage', 'handleSelectionChange', 'Selection changed', {
    total: selection.length,
    added: added?.length || 0,
    removed: removed?.length || 0
  });
};

const clearSelection = (): void => {
  log('DespesasPage', 'clearSelection', 'Clearing selection');
  selectedDespesas.value = [];
  if (dataTableRef.value) {
    dataTableRef.value.clearSelection();
  }
};

// Bulk baixa functionality
const confirmBulkBaixa = (): void => {
  const validDespesas = selectedDespesas.value.filter(d => String(d.despesa_status_id) === '1');

  if (validDespesas.length === 0) {
    eventBus.emit('globalMessage.warn', t('Nenhuma despesa pendente selecionada'));
    return;
  }

  const totalValor = validDespesas.reduce((sum, d) => {
    const valor = parseFloat(String(d.valor)) || parseFloat(String(d.despesa_valor)) || 0;
    return sum + valor;
  }, 0);

  confirmAction({
    message: `${t('Serão baixadas')} ${validDespesas.length} ${t('despesas totalizando')} ${n(totalValor, 'currency')}. ${t('Deseja continuar?')}`,
    header: t('Confirmar Baixa em Lote'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Baixar Despesas'),
    rejectLabel: t('Cancelar'),
    onAccept: () => executeBulkBaixa(validDespesas),
    onReject: () => {
      log('DespesasPage', 'confirmBulkBaixa', 'User cancelled bulk baixa');
    }
  });
};

const executeBulkBaixa = async (despesasToProcess: DespesaListItem[]): Promise<void> => {
  log('DespesasPage', 'executeBulkBaixa', 'Starting bulk baixa', { count: despesasToProcess.length });
  isBulkProcessing.value = true;

  const results: BulkResults = {
    success: [],
    errors: []
  };

  try {
    eventBus.emit('loading.show', t('Processando baixas em lote...'));

    for (const despesa of despesasToProcess) {
      try {
        await baixarDespesa(despesa.despesa_id);
        results.success.push(despesa.despesa_id);
        log('DespesasPage', 'executeBulkBaixa', 'Baixa successful for ID', despesa.despesa_id);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro desconhecido';
        log('DespesasPage', 'executeBulkBaixa', 'Error for ID', despesa.despesa_id, err);
        results.errors.push({
          despesa_id: despesa.despesa_id,
          error: message
        });
      }
    }

    log('DespesasPage', 'executeBulkBaixa', 'Bulk baixa complete', {
      successCount: results.success.length,
      errorCount: results.errors.length
    });

    if (results.success.length > 0 && results.errors.length === 0) {
      eventBus.emit('globalMessage.success', `${results.success.length} ${t('despesas baixadas com sucesso')}`);
    } else if (results.success.length > 0 && results.errors.length > 0) {
      eventBus.emit('globalMessage.warn', `${results.success.length} ${t('despesas baixadas')}, ${results.errors.length} ${t('erros')}`);
    } else if (results.errors.length > 0) {
      eventBus.emit('globalMessage.error', `${t('Erro ao baixar despesas')}: ${results.errors.length} ${t('falhas')}`);
    }

    clearSelection();
    await loadData();

    eventBus.emit('despesa.bulkBaixado', results);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('DespesasPage', 'executeBulkBaixa', 'Critical error', error);
    eventBus.emit('globalMessage.error', t('Erro ao processar baixas em lote') + ': ' + message);
  } finally {
    isBulkProcessing.value = false;
    eventBus.emit('loading.hide');
  }
};

const openModal = (): void => {
  log('DespesasPage', 'openModal', 'Opening new despesa modal via eventBus');
  eventBus.emit('modal.despesas.open', { action: 'new' });
};

const iniciaCsvImport = (): void => {
  log('DespesasPage', 'iniciaCsvImport', 'Opening CSV import modal via eventBus');
  eventBus.emit('modal.despesas-csv-import.open', {});
};

// Event handlers with stable references for cleanup
const handleFilterUpdated = (): void => {
  log('DespesasPage', 'event:filter.updated', 'Received');
  loadData();
};

const handleDespesaSaved = (): void => {
  log('DespesasPage', 'event:despesa.saved', 'Received');
  loadData();
};

const handleCsvImported = (): void => {
  log('DespesasPage', 'event:despesa.csv.imported', 'Received');
  loadData();
};

// Lifecycle hooks
onMounted(async () => {
  log('DespesasPage', 'mounted', 'Component mounted, wiring handlers and loading initial data');

  // Load initial data
  await loadData();

  // Set up event listeners with proper references for cleanup
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('despesa.saved', handleDespesaSaved);
  eventBus.on('despesa.csv.imported', handleCsvImported);
});

onBeforeUnmount(() => {
  log('DespesasPage', 'beforeUnmount', 'Cleaning up listeners and globals');
  // Clean up event listeners properly with callback references
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('despesa.saved', handleDespesaSaved);
  eventBus.off('despesa.csv.imported', handleCsvImported);
});

// Expose public methods for parent components if needed
defineExpose({
  loadData,
  editItem,
  baixarItem,
  openModal,
  iniciaCsvImport,
  clearSelection,
  getSelectedDespesas: () => selectedDespesas.value
});
</script>

<style scoped>
.despesas-page {
  width: 100%;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.empty-state {
  text-align: center;
  padding: 20px;
}

/* Bulk Action Toolbar */
.bulk-action-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.1);
}

.bulk-selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #166534;
}

.bulk-selection-info i {
  font-size: 1.25rem;
  color: #22c55e;
}

.bulk-selection-amount {
  font-weight: 600;
  color: #15803d;
}

.bulk-actions {
  display: flex;
  gap: 0.5rem;
}

/* Disable checkbox for non-pending despesas */
:deep(.row-not-selectable) {
  .p-checkbox {
    pointer-events: none;
    opacity: 0.4;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .bulk-action-toolbar {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }

  .bulk-selection-info {
    justify-content: center;
    flex-wrap: wrap;
  }

  .bulk-actions {
    width: 100%;
    justify-content: center;
  }
}

/* PrimeVue spinner animation */
:deep(.pi-spin) {
  animation: pi-spin 2s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
